import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const MyClass = () => {
  const [classData, setClassData] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMyClass();
  }, []);

  const fetchMyClass = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Récupérer la classe principale du prof
      const res = await fetch('/api/teacher/my-class', {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        throw new Error('Aucune classe principale assignée');
      }

      const data = await res.json();
      setClassData(data.class || data);
      setStudents(data.students || []);
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center text-gray-500">
        Chargement de votre classe...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-6 rounded-lg">
          <h2 className="font-bold mb-2">⚠️ Aucune classe principale</h2>
          <p className="text-sm">
            Vous n'êtes pas encore désigné(e) comme professeur principal d'une classe.
            Contactez l'administration pour plus d'informations.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div>
        <h1 className="text-2xl font-bold text-[#2C3E50]">
          🎯 Ma classe principale
        </h1>
        <p className="text-gray-500">
          Vous êtes le professeur principal de cette classe
        </p>
      </div>

      {/* Carte de la classe */}
      <div className="bg-gradient-to-br from-[#E67E22] to-[#D35400] text-white rounded-2xl p-6 shadow-lg">
        <div className="flex justify-between items-start">
          <div>
            <div className="text-sm opacity-90">Classe principale</div>
            <div className="text-3xl font-bold mt-1">
              {classData?.name || 'Classe'}
            </div>
            <div className="text-sm mt-2 opacity-90">
              📅 Année scolaire : {classData?.academic_year || '2026-2027'}
            </div>
            {classData?.room && (
              <div className="text-sm opacity-90">🏛️ Salle : {classData.room}</div>
            )}
          </div>
          <div className="text-right">
            <div className="text-sm opacity-90">Effectif</div>
            <div className="text-4xl font-bold">
              {students.length}/{classData?.max_students || 40}
            </div>
          </div>
        </div>
      </div>

      {/* Actions rapides */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Link
          to={`/teacher/classes/${classData?.id}`}
          className="bg-white rounded-xl shadow-md hover:shadow-lg transition p-5 text-center"
        >
          <div className="text-3xl mb-2">👨‍🎓</div>
          <div className="font-semibold text-[#2C3E50]">Mes élèves</div>
          <div className="text-xs text-gray-500 mt-1">
            {students.length} élève{students.length > 1 ? 's' : ''}
          </div>
        </Link>

        <Link
          to="/teacher/attendance"
          className="bg-white rounded-xl shadow-md hover:shadow-lg transition p-5 text-center"
        >
          <div className="text-3xl mb-2">📋</div>
          <div className="font-semibold text-[#2C3E50]">Faire l'appel</div>
          <div className="text-xs text-gray-500 mt-1">Présences élèves</div>
        </Link>

        <Link
          to="/teacher/results"
          className="bg-white rounded-xl shadow-md hover:shadow-lg transition p-5 text-center"
        >
          <div className="text-3xl mb-2">📊</div>
          <div className="font-semibold text-[#2C3E50]">Résultats</div>
          <div className="text-xs text-gray-500 mt-1">Moyennes & rangs</div>
        </Link>

        {/* 🎯 NOUVEAU : Lien actif vers la page Présence profs */}
        <Link
          to="/teacher/teacher-attendance"
          className="bg-white rounded-xl shadow-md hover:shadow-lg transition p-5 text-center"
        >
          <div className="text-3xl mb-2">🕐</div>
          <div className="font-semibold text-[#2C3E50]">Présence profs</div>
          <div className="text-xs text-gray-500 mt-1">Suivi quotidien</div>
        </Link>
      </div>

      {/* Liste des élèves */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-lg font-bold text-[#2C3E50] mb-4">
          👨‍🎓 Élèves de la classe ({students.length})
        </h2>

        {students.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            Aucun élève dans cette classe pour l'instant
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">
                    Matricule
                  </th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">
                    Nom
                  </th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">
                    Sexe
                  </th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">
                    Parent
                  </th>
                </tr>
              </thead>
              <tbody>
                {students.map((student) => (
                  <tr key={student.id} className="border-t hover:bg-gray-50">
                    <td className="py-2 px-3 font-mono text-xs text-gray-600">
                      {student.matricule}
                    </td>
                    <td className="py-2 px-3 font-medium">
                      {student.name || student.user?.name || 'Élève'}
                    </td>
                    <td className="py-2 px-3">
                      {student.sexe === 'M' ? '👦 M' : student.sexe === 'F' ? '👧 F' : '—'}
                    </td>
                    <td className="py-2 px-3 text-sm text-gray-600">
                      {student.parent_name || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyClass;